// frontend/src/api/userService.js - VERSION CORRIGÉE

import { secureApiService } from './apiService';
import { isStrongPassword } from '../utils/passwordPolicy';
import { isValidEmail } from '../utils/emailValidation';

// Message unique aligné sur le backend (règle IDENTIQUE : 8 + maj + min + chiffre + spécial).
const WEAK_PWD_MSG = 'Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial.';

// 🔒 VALIDATION DES DONNÉES UTILISATEUR
const validateUserData = (data, isLogin = false) => {
  const sanitized = { ...data };
  
  // Validation email
  if (sanitized.email && !isValidEmail(sanitized.email)) {
    throw new Error('Email invalide');
  }
  
  // Validation mot de passe (pour signup uniquement) — règle UNIQUE alignée sur le backend.
  if (!isLogin && sanitized.mot_de_passe) {
    if (!isStrongPassword(sanitized.mot_de_passe)) {
      throw new Error(WEAK_PWD_MSG);
    }
  }
  
  // Sanitization des noms
  if (sanitized.nom) {
    sanitized.nom = sanitized.nom.replace(/[<>]/g, '').substring(0, 50);
  }
  
  if (sanitized.prenom) {
    sanitized.prenom = sanitized.prenom.replace(/[<>]/g, '').substring(0, 50);
  }
  
  return sanitized;
};

export const userService = {
  // 🔐 AUTHENTIFICATION CLIENT SÉCURISÉE
  signup: (data) => {
    const sanitizedData = validateUserData(data);
    return secureApiService.post("/auth/signup", sanitizedData);
  },

  login: (data) => {
    const sanitizedData = validateUserData(data, true);
    return secureApiService.post("/auth/login", sanitizedData);
  },

  // 🔎 Vérifie DÈS LA SAISIE si un email a déjà un compte (checkout invité) → prévenir tôt
  // « ce compte existe, connectez-vous » au lieu d'attendre le 409 au paiement. Best-effort :
  // ne bloque jamais le parcours (useCache:false pour toujours refléter l'état réel).
  checkEmail: (email) => secureApiService.post("/auth/check-email", { email }, { useCache: false }),

  // 👤 PROFIL UTILISATEUR
  getProfile: () => secureApiService.get("/users/profile"),

  updateProfile: (data) => {
    const sanitizedData = validateUserData(data);
    return secureApiService.put("/users/profile", sanitizedData);
  },

  // 🎫 RÉSERVATIONS CLIENT
  getMyReservations: () =>
    secureApiService.get("/reservations/mes-reservations"),

  cancelReservation: (id, reason = "") => {
    const sanitizedReason = reason.replace(/[<>]/g, '').substring(0, 200);
    return secureApiService.put(`/reservations/${id}/annuler`, {
      reason: sanitizedReason
    });
  },

  // ⭐ AVIS CLIENT
  createAvis: (data) => {
    const sanitizedData = { ...data };
    if (sanitizedData.commentaire) {
      sanitizedData.commentaire = sanitizedData.commentaire
        .replace(/[<>]/g, '')
        .substring(0, 500);
    }
    return secureApiService.post("/avis", sanitizedData);
  },

  getMyAvis: () => secureApiService.get("/avis/mes-avis"),

  // 🔄 MOT DE PASSE
  updatePassword: (currentPassword, newPassword) => {
    if (!currentPassword || !newPassword) {
      throw new Error('Les mots de passe sont requis');
    }
    if (!isStrongPassword(newPassword)) {
      throw new Error(WEAK_PWD_MSG);
    }
    return secureApiService.put("/users/password", {
      currentPassword,
      newPassword
    });
  },

  // 📧 RÉINITIALISATION MOT DE PASSE
  requestPasswordReset: (email) => {
    if (!isValidEmail(email)) throw new Error('Email invalide');
    return secureApiService.post("/auth/forgot-password", { email });
  },

  confirmPasswordReset: (token, newPassword) => {
    if (!token || !newPassword) throw new Error('Token et nouveau mot de passe requis');
    if (!isStrongPassword(newPassword)) throw new Error(WEAK_PWD_MSG);
    return secureApiService.post("/auth/reset-password", { token, newPassword, confirmPassword: newPassword });
  },

  // 🆕 VÉRIFICATION TOKEN
  verifyToken: () => secureApiService.get("/auth/verify"),

  // 📱 OTP SMS - Vérification
  verifyOtp: ({ tempToken, otp }) =>
    secureApiService.post("/auth/verify-otp", { tempToken, otp }),

  // 📱 OTP SMS - Renvoi
  resendOtp: ({ tempToken, email }) =>
    secureApiService.post("/auth/resend-otp", { tempToken, email }),

  // 🖼️ PHOTO DE PROFIL
  updatePhoto: (photo_url) =>
    secureApiService.put("/users/photo", { photo_url }),

  // 🗑️ SUPPRESSION COMPTE (avec verification mot de passe)
  deleteAccount: (password) =>
    secureApiService.delete("/users/delete", { data: { password } }),

  // 🔐 RGPD — export des données personnelles (JSON téléchargeable)
  exportMyData: () =>
    secureApiService.get("/profile/export", { responseType: 'blob' }, { useCache: false }),

  // 🕐 HISTORIQUE CONNEXIONS
  getLoginHistory: () =>
    secureApiService.get("/profile/login-history")
};

export default userService;
